import { date, Screen } from 'quasar'
import { imgixHost, isConsumer, isNative } from 'src/config'
import Discount from 'src/models/discount/Discount'
import Model, { ID, Integer, IsoDate, LocalizedJsonObject, MODEL_LEVEL } from 'src/models/model'
import Librariable from 'src/models/model/concerns/Librariable'
import Movie from 'src/models/movie/Movie'
import Page from 'src/models/page/Page'
import Showing from 'src/models/showing/Showing'
import { prependWithConsumerRoot } from 'src/router/utils'

const { formatDate } = date

export const SECTION_TYPES = {
  CUSTOM: 'Custom',
  DONATION: 'Donation Widget',
  EXPLORE_MOVIES: 'Explore Movies',
  EXPLORE_NEWS: 'Explore News',
  FOOTER: 'Footer',
  GENERIC_CAROUSEL: 'Generic Carousel',
  GENERIC_FORM: 'Generic Form',
  GIFT_CARDS: 'Gift Card Sales Widget',
  HERO_IMAGE: 'Hero Image',
  MARKETING_SIGNUP: 'Marketing Signup',
  NEWS: 'News Carousel',
  POSTERS: 'Poster Carousel',
  QUICK_SHOWTIME: 'Showtime Quick Selector',
  RICH_TEXT: 'Rich Text',
  SHOWTIMES: 'Showtimes',
  SITE_SELECTOR_WITH_ZONES: 'Site Selector with Zones',
  SITE_SELECTOR: 'Site Selector',
  VOUCHERS: 'Voucher Sales Widget'
}
SECTION_TYPES.ALL = _.values(SECTION_TYPES)

SECTION_TYPES.WIDGETS = [
  SECTION_TYPES.DONATION,
  SECTION_TYPES.GENERIC_CAROUSEL,
  SECTION_TYPES.GENERIC_FORM,
  SECTION_TYPES.GIFT_CARDS,
  SECTION_TYPES.MARKETING_SIGNUP,
  SECTION_TYPES.QUICK_SHOWTIME,
  SECTION_TYPES.SITE_SELECTOR,
  SECTION_TYPES.VOUCHERS
]
SECTION_TYPES.MOVIE_WIDGETS = [SECTION_TYPES.EXPLORE_MOVIES, SECTION_TYPES.SHOWTIMES]
SECTION_TYPES.WITH_HEADING = [
  SECTION_TYPES.POSTERS,
  SECTION_TYPES.SHOWTIMES,
  SECTION_TYPES.NEWS,
  SECTION_TYPES.EXPLORE_NEWS,
  SECTION_TYPES.EXPLORE_MOVIES,
  SECTION_TYPES.VOUCHERS,
  SECTION_TYPES.GIFT_CARDS,
  SECTION_TYPES.GENERIC_CAROUSEL,
  SECTION_TYPES.GENERIC_FORM,
  SECTION_TYPES.SITE_SELECTOR_WITH_ZONES,
  SECTION_TYPES.DONATION
]

export const styleSizeOptions = [
  { value: 'xs', label: 'Extra Small' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
  { value: 'xl', label: 'Extra Large' }
]
export const colorOptions = _.map(
  ['default', 'primary', 'secondary', 'header', 'accent', 'dark', 'positive', 'negative', 'info', 'warning', 'custom'],
  color => ({ value: color, label: _.startCase(color) })
)
export const backgroundRepeatOptions = _.map(
  ['repeat', 'repeat-x', 'repeat-y', 'no-repeat'],
  repeat => ({ value: repeat, label: _.startCase(repeat) })
)
export const backgroundAttachmentOptions = _.map(
  ['fixed', 'scroll', 'local'],
  attachement => ({ value: attachement, label: _.startCase(attachement) })
)
export const fontSizeOptions = _.map(
  ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  size => ({ value: size, label: _.startCase(size) })
)
export const HEADING_TYPES = {
  DEFAULT: 'default',
  LINE1: 'Single Line',
  LINE2: 'Double Line',
  VALENTINES1: 'Valentine 1',
  VALENTINES2: 'Valentine 2',
  CUSTOM: 'custom'
}
HEADING_TYPES.ALL = _.values(HEADING_TYPES)
HEADING_TYPES.IMAGES = [HEADING_TYPES.VALENTINES1, HEADING_TYPES.VALENTINES2, HEADING_TYPES.CUSTOM]

export const SELECTOR_DISPLAY_TYPES = {
  DEFAULT: 'show-all-locations-list',
  SEARCH_BOX: 'search-box-only',
  SELECT_BOX: 'select-only-combobox'
}
SELECTOR_DISPLAY_TYPES.ALL = _.values(SELECTOR_DISPLAY_TYPES)
export const SHOWTIMES_VIEW_TYPES = {
  BLOCK: 'block',
  SLIDER: 'slider'
}
export const SHOWTIMES_DISPLAY_TYPES = {
  LIST: 'list',
  GRID: 'grid'
}

export const SHOWTIMES_DATE_PICKER_TYPES = {
  TOGGLE: 'toggle',
  SELECTOR: 'selector',
  SWIPER: 'swiper selector'
}
export const SHOWTIMES_BUTTON_TYPES = {
  BASIC: 'basic',
  DESCRIPTIVE: 'descriptive',
  COMPACT: 'compact'
}

export const WIDGET_DISPLAY_TYPES = {
  EMBED: 'embed',
  POPUP: 'pop-up'
}

export const BUTTON_TYPES = {
  STANDARD: 'Filled',
  UNELEVATED: 'Unelevated',
  OUTLINE: 'Outline',
  FLAT: 'Text'
}
export const DISPLAY_DATE_TYPES = {
  PUBLISHED: 'Published Date',
  START: 'Start Date',
  END: 'End Date'
}
export const TEXT_FIELD_TYPES = {
  OUTLINED: 'Standard',
  ROUNDED: 'Rounded',
  SQUARE: 'Square',
  UNDERLINE: 'Underline'
}
export const POPUP_TRIGGER_TYPES = {
  LOAD: 'When visiting the page',
  SCROLL: 'When scrolling the page',
  EXIT: 'When exiting the page'
}
export const POPUP_FREQUENCY_TYPES = {
  EVERY_TIME: 'Popup everytime',
  DAY: 'Popup every day',
  WEEK: 'Popup every 7 days',
  MONTH: 'Popup every 30 days',
  NEVER: 'Popup one time only'
}
export const IMAGE_POSITION_TYPES = {
  TOP: 'Top',
  LEFT: 'Left',
  RIGHT: 'Right',
  BACKGROUND: 'Background'
}
export const TEXT_POSITION_TYPES = {
  CENTER: 'Center',
  LEFT: 'Left',
  RIGHT: 'Right'
}
export const SITE_INFO_DISPLAY_TYPES = {
  DROPDOWN: 'Dropdown Button',
  BANNER: 'Warning Banner',
  TABS: 'Tabs'
}
export const SITE_SELECTOR_DISPLAY_TYPES = {
  DROPDOWN: 'Dropdown',
  BUTTON: 'Button',
  BLOCK: 'Image Block'
}
export const EXPLORE_MOVIES_FILTER_TYPES = {
  NOW_PLAYING: 'Now Playing',
  COMING_SOON: 'Coming Soon',
  ALL_MOVIES: 'All',
  MY_MOVIES: 'My Movies'
}
EXPLORE_MOVIES_FILTER_TYPES.ALL = _.values(EXPLORE_MOVIES_FILTER_TYPES)

export const VISIBILITY_OPTIONS = {
  DESKTOP_WEB: 'desktop-web',
  MOBILE_WEB: 'mobile-web',
  MOBILE_APP: 'mobile-app'
}
VISIBILITY_OPTIONS.ALL = _.values(VISIBILITY_OPTIONS)
VISIBILITY_OPTIONS.WEB_ONLY = [VISIBILITY_OPTIONS.DESKTOP_WEB, VISIBILITY_OPTIONS.MOBILE_WEB]

export const PREVIEW_OPTIONS = {
  DESKTOP: {
    value: 'desktop',
    label: 'Desktop Web',
    tooltip: 'Preview how the page appears on large screens like desktops and laptops.'
  },
  MOBILE: {
    value: 'mobile',
    label: 'Mobile Web',
    tooltip: 'Preview how the page appears on small screens like smartphones.'
  },
  APP: {
    value: 'app',
    label: 'App',
    tooltip: 'Preview how the page appears within the mobile app interface.'
  }
}
PREVIEW_OPTIONS.ALL = _.values(PREVIEW_OPTIONS)
export default class Section extends Model {
  static name = 'Section'

  static duplicatable = true

  static versioned = true

  static deletable = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.SiteContent]

  static define () {
    this.concerns = [Librariable]

    this.fields = {
      id: {
        type: ID,
        required: true
      },
      draftId: {
        type: ID,
        output: true
      },
      pageId: {
        type: ID
      },
      page: {
        type: Page,
        default: null,
        output: true
      },
      type: {
        type: String,
        default: SECTION_TYPES.CUSTOM,
        options: SECTION_TYPES.ALL
      },
      name: {
        type: String,
        default: null,
        translatable: true
      },
      discount: {
        type: Discount,
        default: null,
        reference: true
      },
      initialNewsItemClassId: {
        type: ID,
        default: null
      },
      newsItemClassIds: {
        type: [ID],
        default: () => ([])
      },
      titleClassId: {
        type: ID,
        default: null
      },
      titleClassIds: {
        type: [ID],
        default: () => ([])
      },
      exploreTitleClassIds: {
        type: [ID],
        default: () => ([])
      },
      exploreFeaturedTitleClassIds: {
        type: [ID],
        default: () => ([])
      },
      showingBadgeIds: {
        type: [ID],
        default: () => ([])
      },
      exploreFeaturedShowingBadgeIds: {
        type: [ID],
        default: () => ([])
      },
      filterShowingBadgeIds: {
        type: [ID],
        default: () => ([])
      },
      filterOptionShowingBadgeIds: {
        type: [ID],
        default: () => ([])
      },
      movies: {
        type: [Movie],
        encode (data) {
          data.movieIds = _.map(this.movies.data, 'id')
        },
        default: () => ([])
      },
      movieIds: {
        type: [ID],
        computed () {
          if (!this.data) return

          if (this.movies) return _.map(this.movies.data, 'id')

          return this.data.movieIds
        },
        encode (data) {
          if (this.movies) {
            data.movieIds = _.map(this.movies.data, 'id')
          } else {
            data.movieIds = this.data.movieIds
          }
        },
        default: () => ([])
      },
      showings: {
        type: [Showing],
        encode (data) {
          data.showingIds = _.map(this.showings.data, 'id')
        },
        default: () => ([])
      },
      showingIds: {
        type: [ID],
        computed () {
          if (!this.data) return

          if (this.showings) return _.map(this.showings.data, 'id')

          return this.data.showingIds
        },
        encode (data) {
          if (this.showings) {
            data.showingIds = _.map(this.showings.data, 'id')
          } else {
            data.showingIds = this.data.showingIds
          }
        },
        default: () => ([])
      },
      config: {
        type: LocalizedJsonObject,
        mergeDefault: true,
        default () {
          return {
            visibility: 'Both', // Both, Desktop Only, Mobile Only
            visible_for: VISIBILITY_OPTIONS.ALL,
            widthMode: 'Centered', // Centered, Full Width, Fixed Full
            showHeading: false,
            marginTop: null,
            marginBottom: null,
            marginSides: 'xs',
            backgroundColor: null,
            backgroundColorHex: null,
            backgroundImage: null,
            backgroundImageFit: null,
            backgroundImagePosition: null,
            backgroundRepeat: null,
            backgroundAttachment: null,

            buttonType: BUTTON_TYPES.STANDARD,
            buttonColor: 'primary',
            buttonColorHex: '#FFFFFF',
            buttonBorderRadius: 4,

            blockBackgroundColor: 'default',
            blockBackgroundColorHex: '#FFFFFF',
            blockPosition: TEXT_POSITION_TYPES.CENTER,

            style: '',
            class: '',

            headingType: 'default',
            headingAlign: null,
            headingColor: 'primary',
            headingColorHex: '#FFFFFF',
            headingBackgroundImage: null,
            headingBackgroundImageRepeat: true,
            headingFontSize: null,

            truncatedLineCount: 9,
            mobileTruncatedLineCount: 3,

            title: null,
            description: null,

            square: true,
            flat: true,

            bannerBackground: true,
            bannerBrightness: 0.3,
            bannerOpacity: 0.8,
            solidShowingBadges: false,
            borderOpacity: 0.9,
            borderBrightness: 0.3,
            showAttendanceLevels: true,
            usePredictedAttendance: false,

            showShowtimesWidgetTitle: true,
            showtimesWidgetTitle: 'Showtimes',

            giftCardSales: [],
            giftCardSalesClasses: [],
            giftCardSalesTypes: [],
            showGiftCardWidgetTitle: true,
            giftCardWidgetTitle: 'Gift Cards',
            showGiftCardWidgetCart: true,
            giftCardSalesShowImage: true,
            giftCardSalesShowName: true,
            giftCardWidgetBackgroundColor: 'custom',
            giftCardWidgetBackgroundColorHex: '#FFFFFF',
            giftCardWidgetTextColor: 'custom',
            giftCardWidgetTextColorHex: '#000000',
            giftCardAllowCustomAmount: false,
            giftCardCustomAmountMinimum: 10,
            giftCardCustomAmountMaximum: 100,

            posterCarouselType: 'current',
            posterCarouselShowFilter: false,
            posterCarouselNumberOfMoviesOnShow: null,
            posterCarouselControlsVisibile: true,
            posterCarouselPaginationVisible: true,
            posterCarouselMaxSlidesPerView: 5.2,
            posterCarouselMobileMaxSlidesPerView: 2.2,
            posterCarouselControlsBackgroundColor: 'default',
            posterCarouselControlsBackgroundColorHex: '#FFFFFF',
            posterCarouselAutoplay: false,
            posterCarouselDelayInSeconds: 6,
            posterCarouselCleanImage: false,
            posterCarouselShowMovieTitle: false,
            posterCarouselShowMovieRating: false,
            posterCarouselShowMovieGenre: false,
            posterCarouselShowUserButton: false,
            posterCarouselShowTrailerButton: false,
            posterCarouselShowTitleClass: false,
            posterCarouselFontSize: 20,
            posterCarouselTextWraped: true,
            posterCarouselTextCenter: false,
            posterCarouselLightText: false,

            showComingSoonDate: false,

            spacing: 10,
            borderRadius: 4,
            shadow: 0,

            newsItemsClassIds: [],

            newsCarousel: null,

            initialNewsItemClassId: null,
            showNewsItemClasses: true,

            showExploreNewsHeading: false,
            showExploreNewsItemBanner: true,
            showExploreNewsItemTitle: true,
            showExploreNewsItemSubtitle: false,
            showExploreNewsItemDescription: true,
            showExploreNewsItemButton: false,
            showExploreNewsItemDate: false,
            showExploreNewsItemClassNames: false,
            isExploreNewsItemTextLight: false,
            isExploreNewsItemTextCentre: true,

            exploreNewsNumberOfNewsOnShow: null,
            exploreNewsItemDateType: DISPLAY_DATE_TYPES.PUBLISHED,
            exploreNewsMaxItemPerRow: 3,
            exploreNewsItemPadding: 15,
            exploreNewsItemBorderRadius: 4,
            exploreNewsItemShadow: 0,
            exploreNewsItemBackgroundColor: null,
            exploreNewsItemBackgroundColorHex: '#ffffff',
            exploreNewsItemKeepImageRatio: false,

            autoplay: true,
            delayInSeconds: 6,

            heroImage: null,
            heroImageHeight: null,
            heroImagePosition: {
              x: 50,
              y: 50
            },
            heroImageActionOptions: ['Movie', 'Page', 'URL'],
            heroImageActionType: null,
            heroImageAction: {},

            exploreMoviesFilterTypes: _(EXPLORE_MOVIES_FILTER_TYPES.ALL).keyBy().mapValues(_ => true).value(),
            exploreType: 'now-playing',
            exploreUserList: 'watched',
            exploreDisplayType: 'posters',
            exploreFilterValue: null,
            exploreTitleClassIds: null,
            exploreFeaturedTitleClassIds: null,
            exploreFeaturedShowingBadgeIds: null,
            exploreCalendarViewDescend: false,
            exploreUsingDarkMode: false,
            explorePosterMaximumPostersPerRow: 3,
            explorePosterMobileMaximumPostersPerRow: 2,
            explorePosterPadding: 8,
            explorePosterBorderRadius: 0,
            explorePosterHasCleanImage: false,
            explorePosterBackgroundColor: null,
            explorePosterBackgroundColorHex: '#ffffff',
            explorePosterFeaturedBackgroundColor: null,
            explorePosterFeaturedBackgroundColorHex: '#ffffff',

            showExploreHeading: false,
            showExploreType: true,
            showExploreUserList: true,
            showExploreDisplayType: true,
            showExploreSearch: true,
            explorePosterShowUserButtons: true,
            explorePosterFontSize: 20,
            explorePosterShowMovieTitle: true,
            explorePosterShowMovieRating: true,
            explorePosterShowMovieGenre: true,
            explorePosterShowMovieSynopsis: false,
            explorePosterMovieSynopsisTruncated: true,
            explorePosterShouldWrapText: false,
            explorePosterHasTextCentred: false,
            explorePosterShowMovieShowingStatus: false,
            explorePosterShowTrailerButton: false,
            exploreMovieShowTitleClass: false,
            explorePosterButtonLabel: null,
            explorePosterButtonType: BUTTON_TYPES.OUTLINE,
            explorePosterButtonColor: 'dark',
            explorePosterButtonColorHex: '#ffffff',
            explorePosterButtonBorderRadius: 4,

            showLogo: true,
            showContactInfo: true,
            showMainLinks: true,
            showMoreLinks: true,
            footerDarkMode: true,
            showSoicalIcons: true,
            socialIconsColor: 'full',
            showCircuitNameInFooter: false,
            verticalAlignTop: true,

            donationTitle: 'Take the time to donate?',
            donationDescription: 'We rely on the generosity of our patrons to keep the doors open.\nThank you for all your support - whether you\'re buying tickets or adding on a little extra.',
            donationButton: 'Donate',

            donationDefaultAmounts: [1, 2, 5],
            donorPerfectCampaignCode: null,
            allowDonationHonoree: false,
            donationLegalName: null,
            sendTaxDeductibleReceipt: false,

            showButton: true,
            showIcon: true,
            showDateSeparators: true,

            voucherSales: [],
            voucherSalesShowImage: true,
            voucherSalesTextAlign: TEXT_POSITION_TYPES.CENTER,
            showVoucherWidgetTitle: true,
            voucherWidgetTitle: 'Vouchers',
            showVoucherWidgetCart: true,
            voucherWidgetBackgroundColor: 'custom',
            voucherWidgetBackgroundColorHex: '#FFFFFF',
            voucherWidgetTextColor: 'custom',
            voucherWidgetTextColorHex: '#000000',

            date: null,
            titleClassId: null,
            titleClassIds: null,
            movies: [],
            showings: [],
            showingBadgeIds: [],

            filterShowingBadgeIds: [],
            matchAllShowingBadges: false,

            useShowingBadgeFilter: false,
            filterOptionShowingBadgeIds: [],

            showSiteSelector: false,

            useBanner: false,
            useDateFilter: true,
            useClassFilter: false,
            cleanImage: false,
            showClass: false,
            showTrailerButton: false,
            enableLeavingSoonBanner: false,
            showDateHeading: false,
            showShowingBadge: true,
            collectName: true,
            collectConsent: false,
            inline: true,
            marketingSignupDisplayType: WIDGET_DISPLAY_TYPES.EMBED,
            marketingSignupPopupTriggerType: 'When Exiting The Page',
            marketingSignupDisplayDelayInSeconds: 6,
            marketingSignupDisplayScrollPercentage: 50,
            marketingSignupPopupFrequencyType: 'Popup Every Day',
            marketingSignupTitle: 'Join Mailing List',
            marketingSignupHasTitle: true,
            marketingSignupHasSubtitle: false,
            marketingSignupSubtitle: '',
            marketingSignupButton: 'Subscribe',
            marketingSignupButtonType: BUTTON_TYPES.OUTLINE,
            marketingSignupButtonColor: 'dark',
            marketingSignupButtonColorHex: '#ffffff',
            marketingSignupButtonBorderRadius: 0,
            marketingSignupTextFieldType: 'Standard',
            marketingSignupHasTextFieldOutline: false,
            marketingSignupHasLightText: false,
            marketingSignupHasImage: false,
            marketingSignupImage: null,
            marketingSignupImagePositionType: IMAGE_POSITION_TYPES.LEFT,

            siteSelectorItems: [],
            siteSelectorTitle: 'Please Select A Location',
            siteSelectorHasTitle: true,
            siteSelectorHasSubtitle: false,
            siteSelectorSubtitle: '',
            siteSelectorHasImage: false,
            siteSelectorImage: null,
            siteSelectorAllowDismiss: false,
            siteSelectorTitleColor: 'default',
            siteSelectorTitleColorHex: '#000000',
            siteSelectorHasLightText: false,
            siteSelectorWidgetDisplayType: WIDGET_DISPLAY_TYPES.POPUP,
            siteSelectorDisplayDelayInSeconds: 6,
            siteSelectorsDisplayType: SITE_SELECTOR_DISPLAY_TYPES.DROPDOWN,
            siteSelectorButtonType: BUTTON_TYPES.OUTLINE,
            siteSelectorButtonColor: 'primary',
            siteSelectorButtonColorHex: '#ffffff',

            siteSelectorBlockBackgroundColor: 'primary',
            siteSelectorBlockBackgroundColorHex: '#ffffff',
            siteSelectorBlockTextColor: 'custom',
            siteSelectorBlockTextColorHex: '#ffffff',
            siteSelectorBlockHasTextCentred: false,
            siteSelectorBlockShadow: 0,
            siteSelectorBorderRadius: 4,
            siteSelectorShowHeading: true,
            siteSelectorInitialDisplayType: SELECTOR_DISPLAY_TYPES.DEFAULT,
            siteSelectorCompactView: false,
            siteSelectorFieldType: 'standard',
            siteSelectorFieldOutline: false,
            siteSelectorCardColor: 'primary',
            siteSelectorCardColorHex: '#ffffff',
            siteSelectorCardBorderRadius: 4,
            siteSelectorLocationButtonType: BUTTON_TYPES.OUTLINE,
            siteSelectorLocationButtonColor: 'custom',
            siteSelectorLocationButtonColorHex: '#ffffff',
            siteSelectorLocationButtonBorderRadius: 4,
            siteSelectorLocationShowSiteImage: false,
            siteSelectorLocationShowSiteDescription: false,
            siteSelectorDefaultExpanded: false,
            siteSelectorRedirectToHomePage: false,

            formName: null,
            notifyEmail: null,
            submissionEmail: null,
            buttonLabel: 'Submit',
            afterSubmitMessage: 'Thanks for your message. We will be in touch!',
            formSubjectBlocks: [],
            formFromBlocks: [],
            formFields: [],

            carousel: null,

            showtimesViewType: 'block',
            showtimesSliderControlsVisible: false,
            showtimesSliderPaginationVisible: true,
            showtimesSliderAutoplay: false,
            showtimesSliderDelayInSeconds: 6,
            showtimesSliderPaginateGroupedSlides: false,
            showtimesSliderGutter: 4,
            showtimesDisplayType: 'list',
            showtimesDisplayTypeAllowToggle: false,
            showtimesSliderPerView: 3,

            showShowtimesWidgetDateFilter: true,
            showtimesDatePickerType: SHOWTIMES_DATE_PICKER_TYPES.SELECTOR,

            showShowtimesWidgetSiteSelector: false,
            showtimesWidgetSiteSelectorDisplayTypes: [],
            showtimesWidgetFlattenZoneGroups: false,

            showtimesListButtonType: SHOWTIMES_BUTTON_TYPES.COMPACT,
            showtimesListButtonUseProgressBar: false,
            showtimesListShowMovieImage: true,
            showtimesListShowMovieSynopsis: true,
            showtimesListMovieSynopsisWrapped: false,

            showtimesGridButtonType: SHOWTIMES_BUTTON_TYPES.COMPACT,
            showtimesGridButtonUseProgressBar: false,
            showtimesGridButtonUseButtonDrawer: false,
            showtimesGridPerRow: 4,
            showtimesBlocksBackgroundColor: null,
            showtimesBlocksBackgroundColorHex: '#FFFFFF',
            showtimesBlocksTextLight: false,

            showtimesButtonType: BUTTON_TYPES.UNELEVATED,
            showtimesButtonColor: 'primary',
            showtimesButtonColorHex: '#FFFFFF',
            showtimesButtonBorderRadius: 4,

            isShowtimesDarkMode: false,
            showtimesAllowAll: false
          }
        },
        translatables: [
          'afterSubmitMessage', 'buttonLabel', 'buttonText', 'description', 'donationButton', 'donationButtonText', 'donationDescription',
          'donationTitle', 'explorePosterButtonLabel', 'giftCardWidgetTitle', 'label', 'image', 'marketingSignupButton', 'marketingSignupSubtitle',
          'marketingSignupTitle', 'mobileImage', 'name', 'showtimesWidgetTitle', 'siteSelectorSubtitle', 'siteSelectorTitle', 'text', 'title',
          'vimeoVideoId', 'voucherWidgetTitle', 'youtubeVideoId'
        ]
      },
      content: {
        type: String,
        default: '',
        excludeFromDefault: true,
        translatable: true
      },
      styles: {
        type: String,
        default: '',
        excludeFromDefault: true
      },
      transformedStyles: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      scopeClass: {
        type: String,
        excludeFromDefault: true,
        output: true
      },
      on: {
        type: Boolean,
        default: true
      },
      startDate: {
        type: IsoDate,
        default: null
      },
      endDate: {
        type: IsoDate,
        default: null
      },
      toDelete: {
        type: Boolean,
        default: false,
        output: true
      },
      displayOrder: {
        type: Integer,
        required: true,
        default: 100
      },
      action: {
        type: String,
        default: null,
        input: true
      }
    }

    this.computed = {
      current () {
        const date = this.configuration.currentSite.siteDate

        return (!this.startDate || date >= this.startDate.toString()) && (!this.endDate || date <= this.endDate.toString())
      },
      visible () {
        return this.on && this.current && !this.heroLinksToHiddenPage
      },
      heroLinksToHiddenPage () {
        if (this.type !== 'Hero Image' || this.config.heroImageActionType !== 'Page' || !this.config.heroImageAction.Page) return false

        const pageSlug = this.config.heroImageAction.Page
        const page = this.configuration.publishedPages.find(page => page.slug === pageSlug)
        return page?.visible === false
      },
      heroImageUrl () {
        if (!this.config.heroImage) return ''

        return `${imgixHost}/${this.config.heroImage}?auto=format,compress`
      },
      heroImageActionUrl () {
        const { heroImageActionType, heroImageAction } = this.config

        if (!heroImageActionType || !heroImageAction[heroImageActionType]) return ''

        if (heroImageActionType === 'URL') {
          return heroImageAction.URL
        } else if (heroImageActionType === 'Movie') {
          const path = `/movie/${heroImageAction.Movie.id}`
          return isConsumer ? prependWithConsumerRoot(path) : path
        } else {
          const path = `/page/${heroImageAction.Page}`
          return isConsumer ? prependWithConsumerRoot(path) : path
        }
      },
      giftCardImageUrl () {
        if (!this.config.giftCardCustomImage) return this.configuration.currentSite.giftCardImageUrl

        return `${imgixHost}/${this.config.giftCardCustomImage}?fit=crop&w=600&h=400&fm=jpeg&auto=format,compress`
      },
      marketingSignupImageUrl () {
        if (!this.config.marketingSignupImage) return ''

        return `${imgixHost}/${this.config.marketingSignupImage}?auto=format,compress`
      },
      siteSelectorImageUrl () {
        if (!this.config.siteSelectorImage) return ''

        return `${imgixHost}/${this.config.siteSelectorImage}?auto=format,compress`
      },
      backgroundImageUrl () {
        if (!this.config.backgroundImage) return null

        return `${imgixHost}/${this.config.backgroundImage}?auto=format,compress`
      },
      headingBackgroundImageUrl () {
        if (!this.config.headingBackgroundImage) return null

        return `${imgixHost}/${this.config.headingBackgroundImage}?fit=fit&auto=format,compress&h=20`
      },
      hasFeatures () {
        return !_.isEmpty(this.exploreFeaturedTitleClassIds) || !_.isEmpty(this.exploreFeaturedShowingBadgeIds)
      },
      linkableId () {
        if (!_.isNil(this.draftId)) return this.id

        return this.page?.originalSectionsByDraftId?.[this.id]?.id
      }
    }
  }

  get name () {
    return this.type
  }

  get visibleFor () {
    return _(this.config.visible_for).keyBy().mapValues(_ => true).value()
  }

  get desktop () {
    return this.visibleFor[VISIBILITY_OPTIONS.DESKTOP_WEB]
  }

  get mobile () {
    return this.visibleFor[VISIBILITY_OPTIONS.MOBILE_WEB]
  }

  get app () {
    return this.visibleFor[VISIBILITY_OPTIONS.MOBILE_APP]
  }

  get desktopOnly () {
    return this.desktop && !this.mobile && !this.app
  }

  get class () {
    const classes = []

    if (this.config.class) {
      classes.push(this.config.class)
    }

    if (this.scopeClass) {
      classes.push(this.scopeClass)
    }

    if (this.config.marginTop) {
      classes.push('q-pt-' + this.config.marginTop)
    }

    if (this.config.marginBottom) {
      classes.push('q-pb-' + this.config.marginBottom)
    }

    if (this.config.marginSides) {
      classes.push('q-px-' + this.config.marginSides)
    }

    if (this.config.marginSides) {
      classes.push(this.scopeClass)
    }

    if (this.config.backgroundColor && this.config.backgroundColor !== 'custom') {
      classes.push('bg-' + this.config.backgroundColor)
    }

    return classes.join(' ')
  }

  get style () {
    const style = []

    if (this.config.widthMode === 'Centered') {
      if (Screen.sm) style.push('max-width: 980px; display: block; margin-left: auto; margin-right: auto;')
      if (Screen.md) style.push('max-width: 1230px; display: block; margin-left: auto; margin-right: auto;')
      if (Screen.lg) style.push('max-width: 1360px; display: block; margin-left: auto; margin-right: auto;')
      if (Screen.xl) style.push('max-width: 1600PX; display: block; margin-left: auto; margin-right: auto;')
    }

    return style.join(';')
  }

  get backgroundStyle () {
    const backgroundStyle = []

    if (this.config.backgroundColor === 'custom' && this.config.backgroundColorHex) {
      backgroundStyle.push('background-color: ' + this.config.backgroundColorHex)
    }
    if (this.config.backgroundImage && this.config.backgroundColor !== 'default') {
      backgroundStyle.push('background-image: ' + `url(${this.backgroundImageUrl})`)
    }
    if (this.config.backgroundImageFit) {
      backgroundStyle.push('background-size: ' + this.config.backgroundImageFit)
    }
    if (this.config.backgroundRepeat) {
      backgroundStyle.push('background-repeat: ' + this.config.backgroundRepeat)
    }
    if (this.config.backgroundImagePosition) {
      backgroundStyle.push('background-position: ' + this.config.backgroundImagePosition)
    }
    if (this.config.backgroundAttachment) {
      backgroundStyle.push('background-attachment: ' + this.config.backgroundAttachment)
    }

    backgroundStyle.push(this.config.style)

    return backgroundStyle.join(';')
  }

  get displayIcons () {
    const icons = []

    if (this.toDelete) {
      return [
        {
          name: 'delete',
          color: 'grey',
          tooltip: 'Section removed'
        }
      ]
    }

    if (!this.on) {
      icons.push({
        name: 'toggle_off',
        color: 'grey',
        tooltip: 'Not Visible',
        handleClick: () => this.setVisible()

      })
    }

    if (this.on) {
      if (this.visible) {
        icons.push({
          name: 'visibility',
          color: 'positive',
          tooltip: 'Visible',
          handleClick: () => this.setNotVisible()
        })
      } else {
        icons.push({
          name: 'visibility_off',
          color: 'negative',
          tooltip: 'Hidden',
          handleClick: () => this.setNotVisible()
        })
      }

      if (this.startDate || this.endDate) {
        const now = this.configuration.currentSite.siteTime
        const startDate = this.startDate && this.startDate.inTimeZone(this.configuration.currentSite.timeZone)
        const endDate = this.endDate && this.endDate.inTimeZone(this.configuration.currentSite.timeZone)

        if (startDate && now <= startDate) {
          icons.push({
            name: 'schedule',
            color: 'positive',
            tooltip: `Starts on ${formatDate(startDate, 'MMM D, YYYY')} (${startDate.relativeTime()})`
          })
        } else if (endDate && now < endDate) {
          icons.push({
            name: 'schedule',
            color: 'negative',
            tooltip: `Ends on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`
          })
        } else if (endDate && now > endDate) {
          icons.push({
            name: 'schedule',
            color: 'grey',
            tooltip: `Ended on ${formatDate(endDate, 'MMM D, YYYY')} (${endDate.relativeTime()})`
          })
        }
      } else {
        icons.push({
          name: 'schedule',
          color: 'grey',
          tooltip: 'Not Scheduled, Always Visible'
        })
      }
    }

    if (this.mobile) {
      icons.push({
        name: 'smartphone',
        color: 'dark',
        tooltip: 'Visible on Mobile Web'
      })
    }

    if (this.desktop) {
      icons.push({
        name: 'computer',
        color: 'dark',
        tooltip: 'Visible on Desktop'
      })
    }

    if (this.app) {
      icons.push({
        name: 'fas fa-grid-2-plus',
        color: 'dark',
        tooltip: 'Visible on Mobile App'
      })
    }

    return icons
  }

  showOnScreen (screen) {
    return !this.toDelete && (
      (this.mobile && screen.lt.sm) ||
      (this.desktop && screen.gt.xs) ||
      (this.app && isNative)
    )
  }

  async setVisible () {
    this.on = true
    await this.save()
  }

  async setNotVisible () {
    this.on = false
    await this.save()
  }

  async undelete () {
    this.action = 'undelete'

    try {
      await this.save()
    } catch (error) {
      console.log(error)
    } finally {
      this.resetField('action')
    }
  }
}
